import { useEffect } from 'react';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '') || 'https://example.com';
const CANONICAL_URL = `${SITE_URL}/`;

export default function SeoJsonLd() {
  useEffect(() => {
    const canonical = document.getElementById('canonical-tag') as HTMLLinkElement | null;
    if (canonical) canonical.href = CANONICAL_URL;

    const ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (ogUrl) ogUrl.content = CANONICAL_URL;
  }, []);

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '家の管理おまかせパック',
    url: CANONICAL_URL,
    inLanguage: 'ja',
    description: '岡山市・倉敷市など岡山県南部の住宅メンテナンスをまるごとお任せ。月額定額で家の点検・修繕・管理を提供するサブスクリプションサービス。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${CANONICAL_URL}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'HomeAndConstructionBusiness',
    '@id': `${CANONICAL_URL}#business`,
    name: '家の管理おまかせパック',
    description:
      '岡山市・倉敷市など岡山県南部の住宅メンテナンスをまるごとお任せ。月額定額で家の点検・修繕・管理を提供するサブスクリプションサービス。',
    url: CANONICAL_URL,
    telephone: '+81-120-847-481',
    image: `https://public.readdy.ai/ai/img_res/5b860c7a8bde212a32dc060ac6e1b86f.jpg`,
    priceRange: '¥980〜',
    currenciesAccepted: 'JPY',
    paymentAccepted: '現金, クレジットカード, 銀行振込',
    areaServed: [
      { '@type': 'City', name: '岡山市', containedInPlace: { '@type': 'State', name: '岡山県' } },
      { '@type': 'City', name: '倉敷市', containedInPlace: { '@type': 'State', name: '岡山県' } },
      { '@type': 'City', name: '玉野市', containedInPlace: { '@type': 'State', name: '岡山県' } },
      { '@type': 'City', name: '総社市', containedInPlace: { '@type': 'State', name: '岡山県' } },
      { '@type': 'City', name: '早島町', containedInPlace: { '@type': 'State', name: '岡山県' } },
      { '@type': 'City', name: '里庄町', containedInPlace: { '@type': 'State', name: '岡山県' } },
    ],
    address: {
      '@type': 'PostalAddress',
      addressRegion: '岡山県',
      addressLocality: '岡山市',
      addressCountry: 'JP',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 34.6617,
      longitude: 133.935,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: '住宅メンテナンスサービス',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '家の管理おまかせパック スタンダードプラン',
            description: '月額定額で住宅の点検・修繕・管理をまるごとお任せ。初月980円キャンペーン実施中。',
          },
          price: '980',
          priceCurrency: 'JPY',
          availability: 'https://schema.org/InStock',
          validFrom: '2026-01-01',
        },
      ],
    },
    sameAs: [],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${CANONICAL_URL}#service`,
    name: '住宅メンテナンス月額定額サービス',
    serviceType: '住宅メンテナンス・管理',
    description: '岡山市・倉敷市など岡山県南部の住宅メンテナンスをまるごとお任せ。定期点検・小修繕・設備確認・庭の簡易管理など、家の維持管理に関わる幅広い作業に対応。作業後はLINEで報告書をお送りします。',
    provider: {
      '@type': 'HomeAndConstructionBusiness',
      '@id': `${CANONICAL_URL}#business`,
      name: '家の管理おまかせパック',
    },
    areaServed: {
      '@type': 'State',
      name: '岡山県',
    },
    offers: {
      '@type': 'Offer',
      name: '初月980円キャンペーン',
      description: '新規ご契約の方を対象に、初月のご利用料金を980円でご提供するキャンペーン。',
      price: '980',
      priceCurrency: 'JPY',
      availability: 'https://schema.org/InStock',
      url: CANONICAL_URL,
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: '住宅メンテナンスメニュー',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '定期点検',
            description: '住宅の定期的な点検・診断サービス',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '小修繕',
            description: '日常的な小さな修繕・補修作業',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '設備確認',
            description: '給排水・電気・ガスなど住宅設備の確認',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '庭の簡易管理',
            description: '庭木の剪定・草取りなど庭の簡易管理',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'LINEレポート',
            description: '作業後にLINEで写真付き報告書を送付',
          },
        },
      ],
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${CANONICAL_URL}#webpage`,
    name: '岡山市・倉敷市の住宅メンテナンスなら家の管理おまかせパック｜月額定額・初月980円',
    description:
      '岡山市・倉敷市など岡山県南部の住宅メンテナンスなら「家の管理おまかせパック」。月額定額で家の点検・修繕・管理をまるごとお任せ。初月980円キャンペーン実施中。シニア世代も安心の丁寧サービス。',
    url: CANONICAL_URL,
    inLanguage: 'ja',
    dateModified: '2026-04-04',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${CANONICAL_URL}#website`,
      name: '家の管理おまかせパック',
      url: CANONICAL_URL,
    },
    about: {
      '@type': 'Service',
      '@id': `${CANONICAL_URL}#service`,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ホーム',
          item: CANONICAL_URL,
        },
      ],
    },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', '.hero-description'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'どのエリアに対応していますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '岡山市・倉敷市・玉野市・総社市・早島町・里庄町など岡山県南部を中心に対応しています。エリア外の方はお気軽にご相談ください。',
        },
      },
      {
        '@type': 'Question',
        name: '初月980円キャンペーンとは何ですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '新規ご契約の方を対象に、初月のご利用料金を980円でご提供するキャンペーンです。通常料金との差額分がお得になります。',
        },
      },
      {
        '@type': 'Question',
        name: 'どのような作業を行ってもらえますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '住宅の定期点検・小修繕・設備の確認・庭の簡易管理など、家の維持管理に関わる幅広い作業に対応しています。作業後はLINEで報告書をお送りします。',
        },
      },
      {
        '@type': 'Question',
        name: '契約の縛りはありますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '長期契約の縛りはございません。月単位でご利用いただけます。詳しくはお問い合わせください。',
        },
      },
      {
        '@type': 'Question',
        name: '月額料金はいくらですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '初月は980円のキャンペーン価格でご利用いただけます。通常料金についてはお問い合わせフォームまたはお電話（0120-847-481）にてご確認ください。',
        },
      },
      {
        '@type': 'Question',
        name: 'シニア世代でも利用できますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'はい、シニア世代のお客様にも安心してご利用いただけるよう、丁寧なサービスを心がけています。LINEでの報告書送付など、わかりやすいコミュニケーションを大切にしています。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}
